/**
 * automationMapper.js
 *
 * Maps between the unified "Automation" frontend model and the
 * existing backend models (FieldTrigger + Workflow).
 *
 * The frontend presents a single "Automation" object to the user.
 * On save, it splits into triggerData + workflowData for the existing APIs.
 * On load, it merges a trigger + workflow into one unified object.
 */

/**
 * Merge a FieldTrigger and its linked Workflow into a single unified model.
 *
 * @param {Object} trigger  - FieldTrigger from backend
 * @param {Object} workflow - Workflow from backend (may be null for trigger-only view)
 * @returns {Object} unified automation model
 */
export function cloneAutomationValue(value) {
  if (Array.isArray(value)) return value.map(cloneAutomationValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneAutomationValue(item)]));
  }
  return value;
}

export function areAutomationValuesEqual(left, right) {
  if (Object.is(left, right)) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;
  if (Array.isArray(left) !== Array.isArray(right)) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every((key) =>
    Object.prototype.hasOwnProperty.call(right, key) && areAutomationValuesEqual(left[key], right[key])
  );
}

// A first binding is different from moving an existing automation. Consult
// both original documents, not a blank label or the draft's current selection.
export function canBindAutomationStructure(unified) {
  const source = unified?._source;
  const missing = value => value == null || value === '';
  return Boolean(source?.trigger && source?.workflow && unified.triggerId && unified.workflowId &&
    source.capabilities?.firstStructureBinding === true &&
    missing(source.trigger.structureId) && missing(source.workflow.structureId));
}

// UI identities must never become persisted IDs on historical nodes that have
// no ID (or duplicate IDs). Keep the association in the editor snapshot instead.
function projectNodes(nodes, prefix, project) {
  const used = new Set();
  const reserved = new Set(nodes.map((node) => node.id).filter(Boolean));
  return nodes.map((node, index) => {
    let id = node.id;
    if (!id || used.has(id) || (prefix === 'step' && ['trigger', 'conditions'].includes(id))) {
      id = `__editor_${prefix}_${index}`;
      while (used.has(id) || reserved.has(id)) id += '_';
    }
    used.add(id);
    return { ...project(cloneAutomationValue(node)), id };
  });
}

export function toUnifiedModel(trigger, workflow, capabilities = {}) {
  const view = {
    // Identifiers
    id: trigger?.id || null,
    triggerId: trigger?.id || null,
    workflowId: trigger?.workflowId || workflow?.id || null,

    // Meta
    name: trigger?.name || workflow?.name || '',
    description: trigger?.description || workflow?.description || '',
    structureId: trigger?.structureId || workflow?.structureId || '',
    isActive: trigger?.isActive ?? workflow?.isActive ?? true,

    // Trigger config
    trigger: {
      type: trigger?.triggerType || 'update',
      approvalEvent: trigger?.approvalEvent || 'completed',
      monitoredFields: trigger?.monitoredFields || [],
      scheduleType: trigger?.scheduleType || 'monthly',
      dayOfMonth: trigger?.dayOfMonth || '1',
      dayOfWeek: trigger?.dayOfWeek ?? '1',
      timeOfDay: trigger?.timeOfDay || '00:00',
      cronExpression: trigger?.cronExpression || '',
      radius: trigger?.radius || null,
      triggerOn: trigger?.triggerOn || 'enter',
    },

    // Conditions (from trigger)
    conditions: projectNodes(trigger?.conditions || [], 'condition', (c) => ({
      ...c,
      type: c.type || 'fieldValue',
      logic: c.operator || 'AND',
      config: {
        ...c.config,
        field: c.config?.field || '',
        operator: c.config?.operator || '==',
        value: c.config?.value ?? '',
      },
    })),

    // Steps (from workflow)
    steps: projectNodes(workflow?.steps || [], 'step', (s) => ({
      ...s,
      name: s.name || '',
      type: s.type || 'updateField',
      config: s.config || {},
      conditions: s.conditions || [],
      outputVariables: s.outputVariables || {},
      stopOnSuccess: s.stopOnSuccess || false,
      stopOnError: s.stopOnError !== false,
    })),

    // Guardrails
    maxExecutionPerDoc: workflow?.maxExecutionPerDoc || null,

    // Metadata (read-only)
    createdAt: trigger?.createdAt || workflow?.createdAt || null,
    lastUpdated: trigger?.lastUpdated || workflow?.lastUpdated || null,
    executionCount: workflow?.executionCount || 0,
    errorCount: workflow?.errorCount || 0,
  };
  // Editor-only, serializable metadata. NEVER include this snapshot in API
  // payloads. Defaults above are presentation defaults, not schema migrations.
  return {
    ...cloneAutomationValue(view),
    _source: {
      trigger: cloneAutomationValue(trigger),
      workflow: cloneAutomationValue(workflow),
      view: cloneAutomationValue(view),
      capabilities: cloneAutomationValue(capabilities),
    },
  };
}

/**
 * Split a unified automation model back into triggerData + workflowData
 * for saving to the existing backend APIs.
 *
 * @param {Object} unified - The unified automation model from the builder
 * @returns {{ triggerData: Object, workflowData: Object }}
 */
export function fromUnifiedModel(unified) {
  if (unified._source) return restoreExistingAutomation(unified);

  const triggerData = {
    name: unified.name,
    description: unified.description,
    structureId: unified.structureId,
    isActive: unified.isActive,
    workflowId: unified.workflowId || '',
    triggerType: unified.trigger?.type || 'update',
    approvalEvent: unified.trigger?.approvalEvent || 'completed',
    monitoredFields: unified.trigger?.monitoredFields || [],
    conditions: (unified.conditions || []).map((c) => ({
      id: c.id,
      type: c.type,
      operator: c.logic || 'AND',
      config: {
        field: c.config?.field || '',
        operator: c.config?.operator || '==',
        value: c.config?.value ?? '',
      },
    })),
    // Schedule fields
    scheduleType: unified.trigger?.scheduleType || 'monthly',
    dayOfMonth: unified.trigger?.dayOfMonth || '1',
    dayOfWeek: unified.trigger?.dayOfWeek ?? '1',
    timeOfDay: unified.trigger?.timeOfDay || '00:00',
    cronExpression: unified.trigger?.cronExpression || '',
    // Proximity fields
    radius: unified.trigger?.radius || null,
    triggerOn: unified.trigger?.triggerOn || 'enter',
  };

  const workflowData = {
    name: unified.name,
    description: unified.description,
    structureId: unified.structureId,
    isActive: unified.isActive,
    triggerType: mapTriggerTypeToWorkflow(unified.trigger?.type),
    maxExecutionPerDoc: unified.maxExecutionPerDoc || null,
    steps: (unified.steps || []).map((s) => ({
      id: s.id,
      name: s.name,
      type: s.type,
      config: s.type === 'addonAction'
        ? { ...(s.config || {}), capability: s.config?.capability || (s.config?.actionKey === 'booking.invite' ? 'booking.invite' : 'message.send') }
        : (s.config || {}),
      conditions: s.conditions || [],
      outputVariables: s.outputVariables || {},
      stopOnSuccess: s.stopOnSuccess || false,
      stopOnError: s.stopOnError !== false,
    })),
  };

  return { triggerData, workflowData };
}

// Apply only differences between the opening view and the edited view. An
// absent property, null, false, 0 or legacy spelling in storage stays exactly
// as it was until that property is explicitly edited.
function applyViewChanges(source, before, after) {
  if (areAutomationValuesEqual(before, after)) return cloneAutomationValue(source);
  if (!after || typeof after !== 'object' || Array.isArray(after) ||
      !before || typeof before !== 'object' || Array.isArray(before)) {
    return cloneAutomationValue(after);
  }
  const result = source && typeof source === 'object' && !Array.isArray(source)
    ? cloneAutomationValue(source) : {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (areAutomationValuesEqual(before[key], after[key]) &&
        Object.prototype.hasOwnProperty.call(before, key) === Object.prototype.hasOwnProperty.call(after, key)) continue;
    if (!Object.prototype.hasOwnProperty.call(after, key)) delete result[key];
    else Object.defineProperty(result, key, {
      value: applyViewChanges(source?.[key], before[key], after[key]),
      writable: true, enumerable: true, configurable: true,
    });
  }
  return result;
}

function restoreNodes(source = [], before = [], after = [], condition = false) {
  return after.map((node) => {
    const index = before.findIndex((old) => old.id === node.id);
    if (index < 0) {
      const created = cloneAutomationValue(node);
      if (condition) {
        created.operator = created.logic || 'AND';
        delete created.logic;
      } else if (created.type === 'addonAction' && !created.config?.capability) {
        created.config = { ...created.config, capability: created.config?.actionKey === 'booking.invite' ? 'booking.invite' : 'message.send' };
      }
      return created;
    }
    if (!condition) return applyViewChanges(source[index], before[index], node);
    const { logic: oldLogic, ...oldView } = before[index];
    const { logic, ...newView } = node;
    const restored = applyViewChanges(source[index], oldView, newView);
    if (!areAutomationValuesEqual(oldLogic, logic)) restored.operator = logic;
    return restored;
  });
}

function restoreExistingAutomation(unified) {
  const { trigger, workflow, view } = unified._source;
  const triggerData = cloneAutomationValue(trigger || {});
  const workflowData = cloneAutomationValue(workflow || {});
  const update = (target, key, before, after) => {
    if (!areAutomationValuesEqual(before, after)) target[key] = cloneAutomationValue(after);
  };
  for (const key of ['name', 'description', 'structureId', 'isActive']) {
    update(triggerData, key, view[key], unified[key]);
    update(workflowData, key, view[key], unified[key]);
  }
  update(triggerData, 'workflowId', view.workflowId, unified.workflowId);
  update(workflowData, 'maxExecutionPerDoc', view.maxExecutionPerDoc, unified.maxExecutionPerDoc);
  for (const key of Object.keys(unified.trigger || {})) {
    update(triggerData, key === 'type' ? 'triggerType' : key, view.trigger?.[key], unified.trigger[key]);
  }
  if (!areAutomationValuesEqual(view.trigger?.type, unified.trigger?.type)) {
    workflowData.triggerType = mapTriggerTypeToWorkflow(unified.trigger?.type);
  }
  if (!areAutomationValuesEqual(view.conditions, unified.conditions)) {
    triggerData.conditions = restoreNodes(trigger?.conditions, view.conditions, unified.conditions, true);
  }
  if (!areAutomationValuesEqual(view.steps, unified.steps)) {
    workflowData.steps = restoreNodes(workflow?.steps, view.steps, unified.steps);
  }
  return { triggerData, workflowData };
}

const TRIGGER_EDITABLE_FIELDS = [
  'name', 'description', 'structureId', 'isActive', 'workflowId', 'triggerType',
  'approvalEvent', 'monitoredFields', 'conditions', 'scheduleType', 'dayOfMonth',
  'dayOfWeek', 'timeOfDay', 'cronExpression', 'radius', 'triggerOn',
];
const WORKFLOW_EDITABLE_FIELDS = [
  'name', 'description', 'structureId', 'isActive', 'triggerType', 'maxExecutionPerDoc', 'steps',
];

export function getAutomationChanges(unified) {
  const { triggerData, workflowData } = fromUnifiedModel(unified);
  const source = unified._source;
  const patch = (before, after, fields) => Object.fromEntries(fields
    .filter((key) => Object.prototype.hasOwnProperty.call(after, key) &&
      (!source || !areAutomationValuesEqual(before?.[key], after[key])))
    .map((key) => [key, after[key]]));
  const progress = unified._saveProgress;
  const triggerPatch = patch(source?.trigger, triggerData, TRIGGER_EDITABLE_FIELDS);
  const workflowPatch = progress?.workflowData
    ? Object.fromEntries(WORKFLOW_EDITABLE_FIELDS
      .filter((key) => Object.prototype.hasOwnProperty.call(workflowData, key) &&
        !areAutomationValuesEqual(progress.workflowData[key], workflowData[key]))
      .map((key) => [key, workflowData[key]]))
    : patch(source?.workflow, workflowData, WORKFLOW_EDITABLE_FIELDS);
  return {
    triggerPatch,
    workflowPatch,
    hasChanges: Object.keys(triggerPatch).length > 0 || Object.keys(workflowPatch).length > 0,
  };
}

/**
 * Map trigger types between the FieldTrigger model and the Workflow model.
 * FieldTrigger uses: update, create, scheduled, manual, approvalCompleted
 * Workflow uses: fieldChange, manual, scheduled, inboundCatch, approvalCompleted
 */
function mapTriggerTypeToWorkflow(triggerType) {
  const mapping = {
    update: 'fieldChange',
    create: 'fieldChange',
    scheduled: 'scheduled',
    manual: 'manual',
    approvalCompleted: 'approvalCompleted',
    proximity: 'proximity',
  };
  return mapping[triggerType] || 'fieldChange';
}

/**
 * Create an empty unified automation model for a new automation.
 * @returns {Object}
 */
export function createEmptyAutomation() {
  return {
    id: null,
    triggerId: null,
    workflowId: null,
    name: '',
    description: '',
    structureId: '',
    isActive: false,
    trigger: {
      type: 'update',
      approvalEvent: 'completed',
      monitoredFields: [],
      scheduleType: 'monthly',
      dayOfMonth: '1',
      dayOfWeek: '1',
      timeOfDay: '00:00',
      cronExpression: '',
      radius: 500,
      triggerOn: 'enter',
    },
    conditions: [],
    steps: [],
    maxExecutionPerDoc: null,
    createdAt: null,
    lastUpdated: null,
    executionCount: 0,
    errorCount: 0,
  };
}

/**
 * Generate a unique step ID.
 * @returns {string}
 */
export function generateStepId() {
  return `step_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

/**
 * Generate a unique condition ID.
 * @returns {string}
 */
export function generateConditionId() {
  return `cond_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}
